import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle, Bell, SteeringWheel, Wrench, Warning, Trophy, Gauge, GasPump, ShieldCheck } from "@phosphor-icons/react";
import InstallAppButton from "@/components/InstallAppButton";
import { fmtYmd } from "@/lib/dateFormat";

/**
 * StaffDashboard — personal cockpit for staff role.
 * Shown instead of the admin dashboard so staff see "today's tasks + my stats"
 * rather than "company-wide KPIs + alerts".
 */
export default function StaffDashboard() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [fuelData, setFuelData] = useState({ recent: [], summary: null });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            api.get("/dashboard/staff-kpis"),
            api.get("/fuel-logs"),
            api.get("/fuel-logs/summary?days=30"),
        ])
            .then(([dash, fuelList, fuelSum]) => {
                setData(dash.data);
                setFuelData({ recent: (fuelList.data || []).slice(0, 5), summary: fuelSum.data });
            })
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return <p className="text-sm text-[var(--prc-muted)] font-mono uppercase tracking-wider animate-pulse" data-testid="staff-dash-loading">// LOADING...</p>;
    }
    if (!data) return null;

    const s = data.stats;
    const unreadAlerts = data.my_alerts.filter((a) => !(a.read_by || []).includes(user?.id)).length;

    return (
        <div className="space-y-5 sm:space-y-6" data-testid="staff-dashboard">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <p className="prc-label-overline">// MY COCKPIT</p>
                    <h1 className="text-xl sm:text-[1.685rem] font-bold tracking-tight truncate">
                        おかえりなさい、{user?.name || user?.email}
                    </h1>
                    <p className="text-xs sm:text-sm text-[var(--prc-muted)] mt-1">本日のタスクと最近の活動ステータス</p>
                </div>
                <InstallAppButton testid="staff-install-app-btn" className="flex-shrink-0" />
            </div>

            {/* Quick actions — large touch targets on mobile */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="staff-quick-actions">
                <Link to="/inspect" className="prc-card p-5 sm:p-4 hover:border-[var(--prc-green)] active:scale-[0.98] transition group" data-testid="staff-quick-inspect">
                    <div className="flex items-center justify-between mb-1">
                        <ShieldCheck size={24} className="text-[var(--prc-green)]" />
                        <span className="text-[var(--prc-muted)] group-hover:text-[var(--prc-green)] transition text-lg">→</span>
                    </div>
                    <p className="font-semibold text-base mt-2">使用前点検</p>
                    <p className="text-xs text-[var(--prc-muted)] mt-0.5">チェックリストを開始</p>
                </Link>
                <Link to="/operations" className="prc-card p-5 sm:p-4 hover:border-[var(--prc-green)] active:scale-[0.98] transition group" data-testid="staff-quick-ops">
                    <div className="flex items-center justify-between mb-1">
                        <SteeringWheel size={24} className="text-sky-300" />
                        <span className="text-[var(--prc-muted)] group-hover:text-[var(--prc-green)] transition text-lg">→</span>
                    </div>
                    <p className="font-semibold text-base mt-2">運行を記録</p>
                    <p className="text-xs text-[var(--prc-muted)] mt-0.5">本日の運行日報</p>
                </Link>
                <Link to="/fuel" className="prc-card p-5 sm:p-4 hover:border-[var(--prc-green)] active:scale-[0.98] transition group" data-testid="staff-quick-fuel">
                    <div className="flex items-center justify-between mb-1">
                        <GasPump size={24} className="text-fuchsia-300" />
                        <span className="text-[var(--prc-muted)] group-hover:text-[var(--prc-green)] transition text-lg">→</span>
                    </div>
                    <p className="font-semibold text-base mt-2">給油を記録</p>
                    <p className="text-xs text-[var(--prc-muted)] mt-0.5">L・単価・領収書</p>
                </Link>
                <Link to="/notifications" className="prc-card p-5 sm:p-4 hover:border-[var(--prc-green)] active:scale-[0.98] transition group" data-testid="staff-quick-notif">
                    <div className="flex items-center justify-between mb-1">
                        <Bell size={24} className="text-amber-300" />
                        <span className="text-[var(--prc-muted)] group-hover:text-[var(--prc-green)] transition text-lg">→</span>
                    </div>
                    <p className="font-semibold text-base mt-2">通知を確認</p>
                    <p className="text-xs text-[var(--prc-muted)] mt-0.5">お知らせ・アラート</p>
                </Link>
            </div>

            {/* Personal KPI strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="staff-kpi-strip">
                <KpiBox
                    icon={<SteeringWheel size={24} />}
                    label="運行回数 (30日)"
                    value={s.trips_30d}
                    sub={`${s.total_km_30d.toLocaleString()} km`}
                    testId="kpi-trips"
                />
                <KpiBox
                    icon={<Gauge size={24} />}
                    label="平均燃費 (30日)"
                    value={s.fuel_efficiency_kmpl != null ? s.fuel_efficiency_kmpl : (fuelData.summary?.avg_kmpl || "—")}
                    sub={
                        fuelData.summary && fuelData.summary.cost > 0
                            ? `¥${fuelData.summary.cost.toLocaleString()} / ${fuelData.summary.liters?.toFixed(1) || 0}L`
                            : (s.fuel_efficiency_kmpl != null ? "km/L" : "給油記録なし")
                    }
                    tone={(s.fuel_efficiency_kmpl ?? fuelData.summary?.avg_kmpl ?? 0) >= 12 ? "good" : "neutral"}
                    testId="kpi-fuel"
                />
                <KpiBox
                    icon={<ShieldCheck size={24} />}
                    label="点検実施 (30日)"
                    value={s.inspections_30d}
                    sub={`完了 ${s.inspections_completed_30d}`}
                    testId="kpi-insp"
                />
                <KpiBox
                    icon={<Warning size={24} />}
                    label="NG検出 (30日)"
                    value={s.inspections_ng_30d}
                    sub={s.inspections_ng_30d > 0 ? "要対応" : "問題なし"}
                    tone={s.inspections_ng_30d > 0 ? "bad" : "good"}
                    testId="kpi-ng"
                />
            </div>

            {/* Today's tasks */}
            <div className="prc-card p-5" data-testid="staff-today">
                <div className="flex items-center justify-between mb-3">
                    <p className="prc-label-overline mb-0">// 本日のタスク</p>
                    <span className="text-xs text-[var(--prc-muted)] font-mono">
                        {new Date().toLocaleDateString("ja-JP", { year: "numeric", month: "long", day: "numeric", weekday: "short" })}
                    </span>
                </div>
                {data.today_inspections.length === 0 ? (
                    <div className="text-center py-6 text-sm text-[var(--prc-muted)]" data-testid="staff-today-empty">
                        <ShieldCheck size={24} className="mx-auto mb-2 text-[var(--prc-muted)]/60" />
                        本日の点検はまだ開始されていません。<br />
                        <Link to="/inspect" className="text-[var(--prc-green)] hover:underline font-semibold">使用前点検を開始</Link> してください。
                    </div>
                ) : (
                    <ul className="divide-y divide-[var(--prc-border)]">
                        {data.today_inspections.map((i) => (
                            <li key={i.id} className="py-2 flex items-center justify-between text-sm" data-testid={`staff-today-insp-${i.id}`}>
                                <div>
                                    <p className="font-semibold">{i.vehicle_plate || i.vehicle_id}</p>
                                    <p className="text-xs text-[var(--prc-muted)]">
                                        開始 {String(i.started_at).substring(11, 16)}
                                        {i.completed_at && ` · 完了 ${String(i.completed_at).substring(11, 16)}`}
                                    </p>
                                </div>
                                <StatusPill status={i.status} overall={i.overall_status} />
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* My alerts + recent activity */}
            <div className="grid md:grid-cols-2 gap-4">
                <div className="prc-card p-5" data-testid="staff-alerts">
                    <div className="flex items-center justify-between mb-3">
                        <p className="prc-label-overline mb-0">// 担当車両の走行距離アラート</p>
                        {unreadAlerts > 0 && (
                            <span className="inline-block px-2 py-0.5 border rounded-sm text-[10px] font-mono uppercase tracking-wider border-rose-400/40 bg-rose-500/10 text-rose-300">
                                未読 {unreadAlerts}
                            </span>
                        )}
                    </div>
                    {data.my_alerts.length === 0 ? (
                        <p className="text-sm text-[var(--prc-muted)] py-4 text-center">担当車両の整備アラートはありません</p>
                    ) : (
                        <ul className="space-y-2 max-h-64 overflow-y-auto pr-1">
                            {data.my_alerts.map((a) => (
                                <li key={a.id} className="border border-[var(--prc-border)] bg-[var(--prc-surface-2)] rounded-sm p-2.5 text-xs" data-testid={`staff-alert-${a.id}`}>
                                    <div className="flex items-center gap-2 mb-1">
                                        <Wrench size={16} className={a.priority === "high" ? "text-rose-300" : "text-amber-300"} />
                                        <p className="font-semibold text-sm">{a.title}</p>
                                    </div>
                                    <p className="text-[var(--prc-muted)] leading-tight">{a.body}</p>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="prc-card p-5" data-testid="staff-recent-ops">
                    <div className="flex items-center justify-between mb-3">
                        <p className="prc-label-overline mb-0">// 最近の運行履歴</p>
                        <Link to="/operations" className="text-xs font-semibold hover:underline">すべて表示 →</Link>
                    </div>
                    {data.recent_operations.length === 0 ? (
                        <p className="text-sm text-[var(--prc-muted)] py-4 text-center">運行記録がありません</p>
                    ) : (
                        <ul className="divide-y divide-[var(--prc-border)]">
                            {data.recent_operations.map((o) => (
                                <li key={o.id} className="py-2 text-sm" data-testid={`staff-op-${o.id}`}>
                                    <div className="flex items-center justify-between">
                                        <span className="font-mono text-xs text-[var(--prc-muted)]">{fmtYmd(o.operation_date, o.operation_date)}</span>
                                        <span className="font-mono text-xs">{o.distance_km != null ? `${o.distance_km} km` : "—"}</span>
                                    </div>
                                    <p className="truncate">
                                        <span className="font-semibold">{o.vehicle_plate}</span>
                                        <span className="text-[var(--prc-muted)] text-xs ml-2">
                                            {o.origin || "—"} → {o.destination || "—"}
                                        </span>
                                    </p>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            {/* Recent fuel logs */}
            <div className="prc-card p-5" data-testid="staff-recent-fuel">
                <div className="flex items-center justify-between mb-3">
                    <p className="prc-label-overline mb-0 inline-flex items-center gap-2">
                        <GasPump size={16} className="text-fuchsia-300" />// 最近の給油履歴
                    </p>
                    <Link to="/fuel" className="text-xs font-semibold hover:underline">すべて表示 →</Link>
                </div>
                {fuelData.recent.length === 0 ? (
                    <p className="text-sm text-[var(--prc-muted)] py-4 text-center" data-testid="staff-fuel-empty">
                        給油記録がありません。<Link to="/fuel" className="text-[var(--prc-green)] hover:underline font-semibold">給油を記録</Link> から登録してください。
                    </p>
                ) : (
                    <ul className="divide-y divide-[var(--prc-border)]">
                        {fuelData.recent.map((f) => {
                            const kmpl = f.km_per_liter;
                            const kmplCls = kmpl >= 12 ? "text-[var(--prc-green)]" : kmpl < 8 ? "text-rose-300" : "text-amber-300";
                            return (
                                <li key={f.id} className="py-2 text-sm" data-testid={`staff-fuel-${f.id}`}>
                                    <div className="flex items-center justify-between">
                                        <span className="font-mono text-xs text-[var(--prc-muted)]">{fmtYmd(f.refueled_at, f.refueled_at)}{f.refueled_time ? ` ${f.refueled_time}` : ""}</span>
                                        {kmpl ? (
                                            <span className={`font-mono text-xs ${kmplCls}`}>{kmpl} km/L</span>
                                        ) : null}
                                    </div>
                                    <div className="flex items-center justify-between mt-0.5">
                                        <p className="truncate min-w-0 flex-1">
                                            <span className="font-semibold">{f.vehicle_plate || "-"}</span>
                                            {f.station_name && <span className="text-[var(--prc-muted)] text-xs ml-2">{f.station_name}</span>}
                                        </p>
                                        <span className="font-mono text-xs flex-shrink-0">
                                            {f.liters} L
                                            {f.total_jpy ? <span className="ml-2 text-[var(--prc-green)]">¥{f.total_jpy.toLocaleString()}</span> : null}
                                        </span>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            {/* Link to full score page */}
            <Link to="/driver-scores" className="prc-card p-4 flex items-center justify-between hover:border-[var(--prc-green)] transition group" data-testid="staff-score-link">
                <div className="flex items-center gap-3">
                    <Trophy size={24} className="text-amber-300" />
                    <div>
                        <p className="font-semibold">自分の運用品質スコアを見る</p>
                        <p className="text-xs text-[var(--prc-muted)]">点検品質 × 安全運転メトリクスのランキング</p>
                    </div>
                </div>
                <span className="text-[var(--prc-muted)] group-hover:text-[var(--prc-green)] transition">→</span>
            </Link>
        </div>
    );
}

function KpiBox({ icon, label, value, sub, tone = "neutral", testId }) {
    const toneCls = tone === "good" ? "text-[var(--prc-green)]"
        : tone === "bad" ? "text-rose-300"
        : "text-white";
    return (
        <div className="prc-card p-4" data-testid={testId}>
            <div className="flex items-center gap-2 text-[var(--prc-muted)] mb-1">{icon}<p className="prc-label-overline mb-0">{label}</p></div>
            <p className={`text-2xl font-bold font-mono mt-1 ${toneCls}`}>{value}</p>
            <p className="text-[10px] font-mono uppercase tracking-wider text-[var(--prc-muted)]">{sub}</p>
        </div>
    );
}

function StatusPill({ status, overall }) {
    if (overall === "ng") return <span className="inline-block px-2 py-0.5 border rounded-sm text-[10px] font-mono uppercase border-rose-400/40 bg-rose-500/10 text-rose-300">NG</span>;
    if (overall === "warn") return <span className="inline-block px-2 py-0.5 border rounded-sm text-[10px] font-mono uppercase border-amber-400/40 bg-amber-500/10 text-amber-300">警告</span>;
    if (overall === "ok") return <span className="inline-block px-2 py-0.5 border rounded-sm text-[10px] font-mono uppercase border-[var(--prc-green,#00ff9c)]/40 bg-[var(--prc-green,#00ff9c)]/10 text-[var(--prc-green,#00ff9c)]">OK</span>;
    if (status === "in_progress") return <span className="inline-block px-2 py-0.5 border rounded-sm text-[10px] font-mono uppercase border-sky-400/40 bg-sky-400/10 text-sky-300">進行中</span>;
    return <span className="inline-block px-2 py-0.5 border rounded-sm text-[10px] font-mono uppercase border-[var(--prc-border)] text-[var(--prc-muted)]">{status}</span>;
}
